import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { describeUploadResult, inspectFile, isRetryableUpload, newIdempotencyKey, uploadPackingListFile } from '../lib/packingListUpload';

const IDLE = { status: 'idle', file: null, key: null, progress: 0, result: null, error: null, retryable: false };

/**
 * PL 원본 업로드 한 건의 상태. 파일을 고르면 Idempotency-Key를 새로 만들어 올리고, 실패한 같은 파일의 Retry는 그 키를 다시 쓴다
 * — 첫 요청이 서버에 등록됐다면 서버가 문서를 또 만들지 않고 그 결과를 돌려준다. 다른 파일을 고르면 새 요청이라 새 키다.
 * 서버 규칙(크기·형식)에 걸리는 파일은 보내지 않고 바로 413·415 안내를 띄운다.
 */
export default function usePackingListUpload(scope) {
    const { getAccessTokenSilently } = useAuth0();
    const [state, setState] = useState(IDLE);
    const abortRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
        };
    }, []);

    const run = useCallback(
        async (file, key) => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setState({ ...IDLE, status: 'uploading', file, key });
            const result = await uploadPackingListFile({
                file,
                scope,
                idempotencyKey: key,
                getAccessTokenSilently,
                signal: controller.signal,
                onUploadProgress: (progress) => mountedRef.current && setState((s) => (s.key === key ? { ...s, progress } : s)),
            });
            if (!mountedRef.current || result.code === 'CANCELLED') return result;
            if (result.ok) setState({ ...IDLE, status: 'done', file, key, progress: 100, result });
            else setState({ ...IDLE, status: 'error', file, key, result, error: describeUploadResult(result), retryable: isRetryableUpload(result) });
            return result;
        },
        [scope, getAccessTokenSilently]
    );

    /** 새 파일을 올린다. 거절될 파일은 요청 없이 안내만 한다. */
    const upload = useCallback(
        (file) => {
            const check = inspectFile(file);
            if (!check.ok) {
                setState({ ...IDLE, status: 'error', file, result: check, error: describeUploadResult(check), retryable: false });
                return Promise.resolve(check);
            }
            return run(file, newIdempotencyKey());
        },
        [run]
    );

    /** 응답을 못 받은 같은 파일을 같은 키로 다시 보낸다. */
    const retry = useCallback(() => (state.file && state.key ? run(state.file, state.key) : Promise.resolve(null)), [run, state.file, state.key]);

    const reset = useCallback(() => {
        abortRef.current?.abort();
        setState(IDLE);
    }, []);

    return { ...state, upload, retry, reset };
}
